// JobNotes.jsx
// The Job notes section of the Job Record: the running log of a job in
// progress. Check-in walkaround and existing damage first, then progress,
// findings, client contact and handover, each with photos and videos.
//
// Everything is stored by the Apps Script (JobNotes.gs): notes and media
// lists in the Quote Log spreadsheet, the files in the job's own Google Drive
// folder (1. Sublime Master Folder → Jobs → "<ref> <client>"). Notes can't be
// edited or deleted, so the log stays a true record; a correction is a new
// note. Removing a photo moves it to the folder's _Removed subfolder.
//
// Uploads: photos are resized here (longest side 2560px) and sent through
// the proxy. Videos and anything large go straight from this browser to
// Drive in 8 MB pieces, using an upload link the Apps Script creates; the
// screen is kept awake while they upload. If that fails, the folder link
// lets you add the file in the Google Drive app, and it shows here anyway.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { apiFetch, formatUkTimestamp } from "./quoteLogger";
import { C, TONE, baseNumber, isTestQuote } from "./quoteUi";
import { JOB_STATUSES } from "./workflow";

const TYPES = ["Check-in", "Progress", "Finding", "Client contact", "Handover"];
const TYPE_TONE = {
  "Check-in": { bg: "#E8ECFB", text: "#2B3E9E", border: "#C9D2F5" },
  Progress: TONE.grey,
  Finding: TONE.amber,
  "Client contact": TONE.green,
  Handover: { bg: "#E8F3F6", text: "#2E6B7A", border: "#C6E0E7" },
};
const PLACEHOLDER = {
  "Check-in": "Walkaround done. Existing damage (where and what), mileage, fuel level, keys received, anything left in the vehicle…",
  Progress: "What's been done so far…",
  Finding: "What we found, where, and what it means for the client (e.g. corroded brake pipe, nearside rear)…",
  "Client contact": "Who we spoke to, how, and what was agreed…",
  Handover: "Work completed, aftercare advice given, anything the client should keep an eye on…",
};
const TOLD_HOW = ["Phone", "WhatsApp", "Email", "In person"];
const HOW_WORDS = { Phone: "by phone", WhatsApp: "by WhatsApp", Email: "by email", "In person": "in person", Other: "" };
const CHUNK = 8 * 1024 * 1024; // a multiple of 256 KB, as Drive requires
const SMALL_LIMIT = 2.8 * 1024 * 1024; // sent through the proxy as base64

const MUTED_LABEL = { fontSize: 11, fontWeight: 500, color: C.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" };
const btn = { padding: "9px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", minHeight: 40 };
const primaryBtn = { ...btn, border: "none", background: C.brand, color: "#fff" };
const linkBtn = { border: "none", background: "none", padding: "4px 6px", margin: "-4px -6px", borderRadius: 6, color: C.brand, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", minHeight: 32 };
const pill = tone => ({ display: "inline-block", fontSize: 11.5, fontWeight: 600, padding: "1px 8px", borderRadius: 999, background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`, whiteSpace: "nowrap" });

async function call(action, body) {
  const res = await apiFetch("/api/sheets-proxy", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, ...body }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

// ─── File handling ──────────────────────────────────────────────────────────
const isVideo = f => /^video\//.test(f.type || "") || /\.(mov|mp4|m4v|webm|3gp)$/i.test(f.name || "");
const isImage = f => /^image\//.test(f.type || "") || /\.(jpe?g|png|heic|heif|webp|gif)$/i.test(f.name || "");
const fmtSize = n => n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(0)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`;

const blobToDataUrl = blob => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = () => reject(r.error); r.readAsDataURL(blob); });

async function decodeImage(file) {
  try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch (e) { return null; }
}
function drawScaled(source, w, h, max) {
  const s = Math.min(1, max / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.round(w * s); c.height = Math.round(h * s);
  c.getContext("2d").drawImage(source, 0, 0, c.width, c.height);
  return c;
}
const canvasBlob = (c, q) => new Promise(r => c.toBlob(r, "image/jpeg", q));

// A still from about a second into a video, for the preview tile.
function videoThumb(file) {
  return new Promise(resolve => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;
    const done = t => { if (settled) return; settled = true; clearTimeout(timer); URL.revokeObjectURL(url); resolve(t || ""); };
    const timer = setTimeout(() => done(""), 6000);
    v.muted = true; v.playsInline = true; v.preload = "auto";
    v.onloadeddata = () => { try { v.currentTime = Math.min(1, (v.duration || 2) / 2); } catch (e) { done(""); } };
    v.onseeked = () => { try { done(drawScaled(v, v.videoWidth, v.videoHeight, 400).toDataURL("image/jpeg", 0.7)); } catch (e) { done(""); } };
    v.onerror = () => done("");
    v.src = url;
  });
}

function putChunk(url, blob, start, total, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${start + blob.size - 1}/${total}`);
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(start + e.loaded); };
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
    xhr.onerror = () => reject(new Error("The upload was blocked or the connection dropped."));
    xhr.send(blob);
  });
}

// Sends a big file straight to Drive in pieces. Returns the Drive file ID.
async function resumableUpload(url, file, onProgress) {
  const total = file.size;
  let start = 0;
  while (start < total) {
    const end = Math.min(start + CHUNK, total);
    let res = null, lastErr = null;
    for (let attempt = 0; attempt < 3 && !res; attempt++) {
      try {
        const r = await putChunk(url, file.slice(start, end), start, total, onProgress);
        if (r.status === 308 || r.status === 200 || r.status === 201) res = r;
        else lastErr = new Error(`Drive said ${r.status}`);
      } catch (e) { lastErr = e; }
      if (!res) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
    }
    if (!res) throw lastErr || new Error("Upload failed.");
    if (res.status === 200 || res.status === 201) {
      onProgress(total);
      try { return JSON.parse(res.body).id; } catch (e) { throw new Error("Drive didn't say which file it saved."); }
    }
    start = end;
  }
  throw new Error("Upload ended early.");
}

// ─── Component ──────────────────────────────────────────────────────────────
export default function JobNotes({ q, author, isMobile, onFlagsChange }) {
  const base = baseNumber(q?.["Quote Number"]);
  const clientName = q?.["Client Name"] || "";
  const test = isTestQuote(q);
  const ctx = { base, clientName, test };

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [composer, setComposer] = useState(null); // { type, text, files, clientToTell }
  const [uploads, setUploads] = useState([]); // [{ key, name, size, pct, state, error }]
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [telling, setTelling] = useState(null); // note id
  const [busyId, setBusyId] = useState(null);
  const [sharing, setSharing] = useState(null); // { noteId, state: "preparing" | "ready" | "error", files, text, error }
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    if (!base) return;
    try {
      setLoadError(null);
      setData(await call("jobNotesGet", ctx));
    } catch (e) { setLoadError(e.message); }
  }, [base, clientName, test]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!base) return null;
  const notes = (data?.notes || []).slice().sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  const mediaByNote = {};
  (data?.media || []).forEach(m => { (mediaByNote[m.noteId] = mediaByNote[m.noteId] || []).push(m); });
  const hasCheckIn = notes.some(n => n.type === "Check-in");
  const booked = [...JOB_STATUSES, "Invoiced"].includes(q["Status"]);
  const folderUrl = data?.folder?.url;

  const openComposer = type => { setSaveError(null); setUploads([]); setComposer({ type, text: "", files: [], clientToTell: type === "Finding" }); };
  const setUpload = (key, patch) => setUploads(list => list.map(u => u.key === key ? { ...u, ...patch } : u));

  async function uploadOne(noteId, file, key) {
    const by = author;
    if (isImage(file) && !isVideo(file)) {
      const bmp = await decodeImage(file);
      if (bmp) {
        const full = await canvasBlob(drawScaled(bmp, bmp.width, bmp.height, 2560), 0.85);
        const thumb = drawScaled(bmp, bmp.width, bmp.height, 400).toDataURL("image/jpeg", 0.7);
        if (full && full.size <= SMALL_LIMIT) {
          setUpload(key, { pct: 30 });
          const dataUrl = await blobToDataUrl(full);
          const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
          await call("jobMediaUploadSmall", { ...ctx, noteId, name, mimeType: "image/jpeg", data: dataUrl.split(",")[1], thumb, by });
          return;
        }
        return big(noteId, full ? new File([full], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file, thumb, key);
      }
    }
    const thumb = isVideo(file) ? await videoThumb(file) : "";
    if (!isVideo(file) && file.size <= SMALL_LIMIT) {
      const dataUrl = await blobToDataUrl(file);
      await call("jobMediaUploadSmall", { ...ctx, noteId, name: file.name, mimeType: file.type || "application/octet-stream", data: dataUrl.split(",")[1], thumb, by });
      return;
    }
    return big(noteId, file, thumb, key);
  }

  async function big(noteId, file, thumb, key) {
    const { uploadUrl } = await call("jobMediaStartUpload", { ...ctx, noteId, name: file.name, mimeType: file.type || "application/octet-stream", size: file.size, origin: window.location.origin });
    const fileId = await resumableUpload(uploadUrl, file, done => setUpload(key, { pct: Math.min(99, Math.round(done / file.size * 100)) }));
    await call("jobMediaRegister", { ...ctx, noteId, fileId, thumb, by: author });
  }

  async function save() {
    if (!composer || saving) return;
    if (!composer.text.trim() && !composer.files.length) { setSaveError("Add a note or some photos first."); return; }
    setSaving(true); setSaveError(null);
    let wake = null;
    try { wake = await navigator.wakeLock?.request("screen"); } catch (e) { /* not supported */ }
    try {
      const { note } = await call("jobNoteAdd", { ...ctx, quoteNumber: q["Quote Number"], author, type: composer.type, text: composer.text.trim(), clientToTell: composer.clientToTell });
      const list = composer.files.map((f, i) => ({ key: `${note.id}-${i}`, name: f.name, size: f.size, pct: 0, state: "waiting" }));
      setUploads(list);
      let failed = 0;
      for (let i = 0; i < composer.files.length; i++) {
        const key = list[i].key;
        setUpload(key, { state: "uploading", pct: 2 });
        try { await uploadOne(note.id, composer.files[i], key); setUpload(key, { state: "done", pct: 100 }); }
        catch (e) { failed++; setUpload(key, { state: "error", error: e.message }); }
      }
      await load();
      onFlagsChange && onFlagsChange();
      if (!failed) { setComposer(null); setUploads([]); }
      else setComposer(c => ({ ...c, files: [], text: "", saved: true }));
    } catch (e) {
      setSaveError(`Couldn't save the note: ${e.message}`);
    } finally {
      try { await wake?.release(); } catch (e) { /* already released */ }
      setSaving(false);
    }
  }

  async function markTold(note, how) {
    setBusyId(note.id);
    try { await call("jobNoteMarkTold", { noteId: note.id, how, by: author }); setTelling(null); await load(); onFlagsChange && onFlagsChange(); }
    catch (e) { window.alert(`Couldn't save: ${e.message}`); }
    finally { setBusyId(null); }
  }

  // "Send to client": the phone's share sheet with the note's photos attached
  // and a message written, so it's tap WhatsApp, pick the client, send. Two
  // taps on purpose: the first fetches the photos, the second opens the share
  // sheet (iPhone only allows sharing straight from a tap).
  const firstWord = s => String(s || "").trim().split(/\s+/)[0] || "";
  const vehicle = (() => { try { const v = JSON.parse(q["State JSON"] || "{}").vehicle || {}; return [v.make, v.model].filter(Boolean).join(" "); } catch (e) { return ""; } })();
  const messageFor = (note, photoCount) => {
    const hi = firstWord(clientName) ? `Hi ${firstWord(clientName)},` : "Hi,";
    const what = vehicle ? `your ${vehicle}` : "your vehicle";
    return `${hi} while working on ${what} we found something we wanted to show you:\n\n${note.text || ""}\n\n${photoCount ? `${photoCount === 1 ? "Photo" : "Photos"} attached. ` : ""}Give us a call on 01736 804118 or reply here if you'd like to talk it through.\n\n${({ Edward: "Ed" })[firstWord(author)] || firstWord(author) || "The team"}, Sublime Precision`;
  };
  async function prepareShare(note) {
    const photos = (mediaByNote[note.id] || []).filter(m => /^image\//.test(m.mimeType || "")).slice(0, 4);
    setSharing({ noteId: note.id, state: "preparing" });
    try {
      const files = [];
      for (const m of photos) {
        const r = await call("jobMediaGet", { fileId: m.id });
        const bytes = Uint8Array.from(atob(r.data), c => c.charCodeAt(0));
        files.push(new File([bytes], r.name || "photo.jpg", { type: r.mimeType || "image/jpeg" }));
      }
      setSharing({ noteId: note.id, state: "ready", files, text: messageFor(note, files.length) });
    } catch (e) { setSharing({ noteId: note.id, state: "error", error: e.message }); }
  }
  function waLink(text) {
    let num = String(q["Client Phone"] || "").replace(/[^\d+]/g, "");
    if (num.startsWith("+")) num = num.slice(1); else if (num.startsWith("00")) num = num.slice(2); else if (num.startsWith("0")) num = "44" + num.slice(1);
    return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
  }
  async function doShare(note) {
    const { files = [], text = "" } = sharing || {};
    const canFiles = files.length && navigator.canShare && navigator.canShare({ files });
    if (navigator.share && (canFiles || !files.length) && isMobile) {
      try {
        await navigator.share(canFiles ? { files, text } : { text });
        setSharing(null); setTelling(note.id);
      } catch (e) { if (e.name !== "AbortError") setSharing(s => ({ ...s, state: "error", error: e.message })); }
      return;
    }
    // Laptop: download the photos and open WhatsApp with the message ready.
    files.forEach(f => { const a = document.createElement("a"); a.href = URL.createObjectURL(f); a.download = f.name; document.body.appendChild(a); a.click(); a.remove(); });
    window.open(waLink(text), "_blank", "noopener");
    setSharing({ noteId: note.id, state: "sent-desktop" });
    setTelling(note.id);
  }

  async function remove(m) {
    if (!window.confirm(`Remove ${m.name} from this job? It moves to the job folder's _Removed folder in Google Drive, so it can be got back.`)) return;
    setBusyId(m.id);
    try { await call("jobMediaRemove", { fileId: m.id, by: author }); await load(); }
    catch (e) { window.alert(`Couldn't remove: ${e.message}`); }
    finally { setBusyId(null); }
  }

  const Tile = ({ m, removable }) => {
    const video = /^video\//.test(m.mimeType || "");
    return (
      <div style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 8, overflow: "hidden", background: "#EEF1F8", border: `1px solid ${C.border}` }}>
        <a href={m.url} target="_blank" rel="noopener noreferrer" title={m.name} style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", color: C.textMuted, fontSize: 11, textAlign: "center", padding: 4 }}>
          {m.thumb ? <img src={m.thumb} alt={m.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} /> : (video ? null : <span style={{ overflowWrap: "anywhere" }}>{m.name}</span>)}
          {video && <span aria-hidden="true" style={{ position: "relative", width: 34, height: 34, borderRadius: "50%", background: "rgba(14,21,32,0.65)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, paddingLeft: 3 }}>▶</span>}
        </a>
        {removable && (
          <button type="button" onClick={() => remove(m)} disabled={busyId === m.id} aria-label={`Remove ${m.name}`} title="Remove"
            style={{ position: "absolute", top: 4, right: 4, width: 26, height: 26, borderRadius: "50%", border: "none", background: "rgba(14,21,32,0.6)", color: "#fff", fontSize: 14, lineHeight: "26px", cursor: "pointer", padding: 0 }}>×</button>
        )}
      </div>
    );
  };
  const grid = { display: "grid", gridTemplateColumns: `repeat(${isMobile ? 3 : 5}, minmax(0, 1fr))`, gap: 8, marginTop: 8 };

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, minHeight: 24 }}>
      <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", flex: 1 }}>Job notes</h2>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {folderUrl && <a href={folderUrl} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, margin: 0, padding: "4px 0", display: "inline-flex", alignItems: "center", textDecoration: "none" }}>Drive folder ↗</a>}
        {!composer && data && <button type="button" onClick={() => openComposer(hasCheckIn || !booked ? "Progress" : "Check-in")} style={{ ...linkBtn, margin: 0, padding: "4px 0" }}>+ Add note</button>}
      </div>
    </div>
  );

  return (
    <section id="record-jobnotes" style={{ padding: isMobile ? "14px 16px 16px" : "16px 24px 18px", borderTop: `1px solid ${C.border}`, scrollMarginTop: isMobile ? 130 : 96 }}>
      {header}

      {loadError && <div style={{ fontSize: 13.5, color: TONE.red.text }}>Couldn't load the job notes: {loadError} <button type="button" onClick={load} style={linkBtn}>Try again</button></div>}
      {!data && !loadError && <div style={{ fontSize: 13.5, color: C.textFaint }}>Loading job notes…</div>}

      {/* Booked and no check-in yet: prompt for the walkaround first. */}
      {data && booked && !hasCheckIn && !composer && (
        <div style={{ background: "#F2F5FE", border: "1px solid #C9D2F5", borderRadius: 10, padding: "12px 14px", marginBottom: 12, display: "flex", alignItems: isMobile ? "stretch" : "center", gap: 12, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ flex: 1, fontSize: 13.5, color: C.text, lineHeight: 1.45 }}>
            <strong>Vehicle check-in.</strong> Before work starts, film a slow walkaround of the vehicle and note any existing damage, the mileage and the keys.
          </div>
          <button type="button" onClick={() => openComposer("Check-in")} style={primaryBtn}>Start check-in</button>
        </div>
      )}

      {data && !notes.length && !composer && !(booked && !hasCheckIn) && <div style={{ fontSize: 13.5, color: C.textFaint }}>No notes yet.</div>}

      {/* The log, oldest first: check-in at the top, handover at the end. */}
      {notes.map(n => {
        const media = mediaByNote[n.id] || [];
        const tone = TYPE_TONE[n.type] || TONE.grey;
        const waiting = n.clientToTell && !n.toldAt;
        return (
          <div key={n.id} style={{ padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={pill(tone)}>{n.type}</span>
              <span style={{ fontSize: 12, color: C.textFaint }}>{formatUkTimestamp(n.createdAt)}{n.author ? ` · ${n.author}` : ""}</span>
              {n.quoteNumber && n.quoteNumber !== q["Quote Number"] && <span style={{ fontSize: 12, color: C.textFaint }}>· on {n.quoteNumber}</span>}
            </div>
            {n.text && <div style={{ marginTop: 4, fontSize: 13.5, color: C.text, lineHeight: 1.5, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{n.text}</div>}
            {media.length > 0 && <div style={grid}>{media.map(m => <Tile key={m.id} m={m} removable />)}</div>}
            {n.clientToTell && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {waiting ? (
                  <>
                    <span style={pill(TONE.amber)}>Client to be told</span>
                    {telling === n.id ? (
                      <>
                        <span style={{ fontSize: 12.5, color: C.textMuted }}>How?</span>
                        {TOLD_HOW.map(h => <button key={h} type="button" disabled={busyId === n.id} onClick={() => markTold(n, h)} style={{ ...btn, padding: "6px 10px", minHeight: 34, fontSize: 12.5 }}>{h}</button>)}
                        <button type="button" onClick={() => setTelling(null)} style={linkBtn}>Cancel</button>
                      </>
                    ) : (
                      <>
                        {sharing?.noteId === n.id && sharing.state === "ready" ? (
                          <button type="button" onClick={() => doShare(n)} style={{ ...primaryBtn, padding: "6px 12px", minHeight: 34, fontSize: 12.5 }}>{isMobile ? "Share now ↗" : "Open WhatsApp ↗"}</button>
                        ) : (
                          <button type="button" disabled={sharing?.noteId === n.id && sharing.state === "preparing"} onClick={() => prepareShare(n)} style={{ ...btn, padding: "6px 12px", minHeight: 34, fontSize: 12.5 }}>{sharing?.noteId === n.id && sharing.state === "preparing" ? "Getting photos…" : "Send to client"}</button>
                        )}
                        <button type="button" onClick={() => setTelling(n.id)} style={linkBtn}>Mark as told</button>
                      </>
                    )}
                  </>
                ) : (
                  <span style={{ ...pill(TONE.green), whiteSpace: "normal", lineHeight: 1.4, padding: "2px 10px" }}>Client told {HOW_WORDS[n.toldHow] || ""} · {formatUkTimestamp(n.toldAt)}{n.toldBy ? ` · ${n.toldBy}` : ""}</span>
                )}
              </div>
            )}
            {sharing?.noteId === n.id && sharing.state === "error" && <div style={{ marginTop: 6, fontSize: 12.5, color: TONE.red.text }}>Couldn't get the photos ready: {sharing.error}</div>}
            {sharing?.noteId === n.id && sharing.state === "sent-desktop" && <div style={{ marginTop: 6, fontSize: 12.5, color: C.textMuted }}>The photos are in your Downloads: drag them into the WhatsApp chat. Then mark it as told.</div>}
          </div>
        );
      })}

      {/* Files put straight into the job folder in Google Drive. */}
      {data?.other?.length > 0 && (
        <div style={{ padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
          <div style={MUTED_LABEL}>Added in Google Drive ({data.other.length})</div>
          <div style={grid}>{data.other.map(m => <Tile key={m.id} m={m} />)}</div>
        </div>
      )}

      {/* New note */}
      {composer && (
        <div style={{ marginTop: notes.length ? 10 : 0, padding: 14, border: `1px solid ${C.border}`, borderRadius: 10, background: "#FAFBFE" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {TYPES.map(t => (
              <button key={t} type="button" disabled={saving} onClick={() => setComposer(c => ({ ...c, type: t, clientToTell: t === "Finding" ? true : c.clientToTell && c.type !== "Finding" }))}
                style={{ ...pill(composer.type === t ? (TYPE_TONE[t] || TONE.grey) : { bg: "#fff", text: C.textMuted, border: C.border }), padding: "6px 12px", fontSize: 12.5, cursor: "pointer", minHeight: 32, fontFamily: "inherit" }}>{t}</button>
            ))}
          </div>
          <textarea value={composer.text} disabled={saving} onChange={e => setComposer(c => ({ ...c, text: e.target.value }))} rows={4} maxLength={5000}
            placeholder={PLACEHOLDER[composer.type]}
            style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: 14, padding: 10, border: `1px solid ${C.border}`, borderRadius: 8, resize: "vertical", background: "#fff" }} />

          <input ref={fileInput} type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
            onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ""; setComposer(c => ({ ...c, files: [...c.files, ...files] })); }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button type="button" disabled={saving} onClick={() => fileInput.current?.click()} style={btn}>{composer.type === "Check-in" ? "+ Walkaround video / photos" : "+ Photos / videos"}</button>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, cursor: "pointer" }}>
              <input type="checkbox" checked={composer.clientToTell} disabled={saving} onChange={e => setComposer(c => ({ ...c, clientToTell: e.target.checked }))} style={{ width: 18, height: 18 }} />
              Client needs to be told
            </label>
          </div>

          {composer.files.length > 0 && !saving && (
            <div style={{ marginTop: 10 }}>
              {composer.files.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0" }}>
                  <span style={{ color: C.textMuted }}>{isVideo(f) ? "Video" : "Photo"}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  <span style={{ color: C.textFaint }}>{fmtSize(f.size)}</span>
                  <button type="button" onClick={() => setComposer(c => ({ ...c, files: c.files.filter((_, j) => j !== i) }))} style={linkBtn}>Remove</button>
                </div>
              ))}
              {composer.files.some(isVideo) && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Videos upload straight to Google Drive. Keep this screen open until they finish.</div>}
            </div>
          )}

          {uploads.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {uploads.map(u => (
                <div key={u.key} style={{ fontSize: 12.5, padding: "4px 0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name} <span style={{ color: C.textFaint }}>{fmtSize(u.size)}</span></span>
                    <span style={{ color: u.state === "error" ? TONE.red.text : u.state === "done" ? TONE.green.text : C.textMuted, whiteSpace: "nowrap" }}>
                      {u.state === "done" ? "Uploaded" : u.state === "error" ? "Failed" : u.state === "waiting" ? "Waiting" : `${u.pct}%`}
                    </span>
                  </div>
                  {u.state !== "error" && <div style={{ height: 4, background: "#E6E9F2", borderRadius: 2, marginTop: 3 }}><div style={{ height: 4, width: `${u.pct}%`, background: u.state === "done" ? "#6C8560" : C.brand, borderRadius: 2, transition: "width 0.2s" }} /></div>}
                  {u.state === "error" && (
                    <div style={{ color: TONE.red.text, marginTop: 2 }}>
                      {u.error} {folderUrl && <>You can add it in the <a href={folderUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.brand }}>job's Drive folder</a> instead and it will show here.</>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {saveError && <div style={{ marginTop: 10, fontSize: 13, color: TONE.red.text }}>{saveError}</div>}
          {composer.saved && !saving && <div style={{ marginTop: 10, fontSize: 13, color: C.textMuted }}>The note is saved. Files marked Failed weren't added.</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <button type="button" disabled={saving} onClick={() => { setComposer(null); setUploads([]); setSaveError(null); }} style={btn}>{composer.saved ? "Close" : "Cancel"}</button>
            {!composer.saved && <button type="button" disabled={saving} onClick={save} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save note"}</button>}
          </div>
        </div>
      )}
    </section>
  );
}
